import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {compact, isEmpty, pickBy} from 'lodash';
import {useField} from 'formik';

import req from 'shared/req';
import Label from 'shared/components/label';
import Dropdown from 'src/components/dropdown';
import TextButton from 'src/components/TextButton';
import SchoolList from 'shared/school-list/components/school-list';
import NoEligibleSchoolsFound from 'src/components/NoEligibleSchoolsFound';

const propTypes = {
  introContent: PropTypes.node,
  onSwitchCountry: PropTypes.func,
  countries: PropTypes.array,
  allowOpenHSChooser: PropTypes.bool,
  openRequestInstitutionModal: PropTypes.func,
  fieldName: PropTypes.string,
  onSelect: PropTypes.func,
};

const InstitutionChooser = ({
  introContent,
  countries,
  fieldName,
  allowOpenHSChooser = false,
  openRequestInstitutionModal = () => {},
  onSwitchCountry = () => {},
  onSelect = () => {},
}) => {
  const [states, setStates] = useState([]);
  const [statesLoading, setStatesLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(true);
  const [highSchools, setHighSchools] = useState([]);
  const [field, _meta, helpers] = useField(fieldName);

  useEffect(() => {
    const {city, subdivision, country} = field.value;

    if (country === 'US') {
      refreshUSStates();
      if (subdivision) {
        refreshCities(country, subdivision);
      }
      if (city) {
        refreshHighSchools(country, subdivision, city);
      }
    } else {
      refreshCities(country);
      refreshHighSchools(country, null, city);
    }
  }, []);

  const onCountrySelect = (e) => {
    const country = e.target.value;

    if (country === 'US') {
      helpers.setValue({
        ...field.value,
        country: country,
        subdivision: '',
      });

      refreshUSStates();
    } else {
      refreshCities(country);
    }

    setHighSchools([]);
    setCities([]);
    setStates([]);

    onSwitchCountry(country);
  };

  const onCitySelect = (e) => {
    const city = e.target.value;

    helpers.setValue({...field.value, city});

    refreshHighSchools(field.value.country, field.value.subdivision, city);
  };

  const onStateSelect = (e) => {
    const state = e.target.value;

    setCitiesLoading(true);
    setCities([]);
    setHighSchools([]);

    refreshCities(field.value.country, state);
  };

  const refreshUSStates = () => {
    setStatesLoading(true);

    return req({
      url: '/v1/lists/us-states',
      method: 'get',
    }).then((response) => {
      setStatesLoading(false);
      setStates(
        response.map(({name, code}) => ({
          value: code,
          text: name,
        }))
      );
    });
  };

  const refreshCities = (country, subdivision = null) => {
    if (isEmpty(country)) {
      return Promise.resolve();
    }

    setCitiesLoading(true);

    return req({
      url: '/v1/cities',
      method: 'get',
      data: pickBy({country, subdivision}),
    }).then(({cities: citiesResponse}) => {
      setCitiesLoading(false);
      setCities(citiesResponse.sort().map((name) => ({value: name, text: name})));

      helpers.setValue({
        ...field.value,
        country,
        subdivision: subdivision || '',
        city: '',
        ineligibleCountry: country !== 'US' && citiesResponse.length === 0,
      });
    });
  };

  const refreshHighSchools = (country, subdivision = null, city = null) => {
    if (compact([country, subdivision, city]).length < 2) {
      return Promise.resolve();
    }

    return req({
      url: '/v1/schools',
      method: 'get',
      data: pickBy({
        country,
        subdivision: country === 'US' && subdivision,
        city,
      }),
    }).then((response) => {
      setHighSchools(
        response.map((school) => ({
          id: school.id,
          name: school.fields.schoolName,
          city: school.fields.city,
          country_code: school.fields.countryCode,
        }))
      );
    });
  };

  const schoolLabelTemplate =
    '${name}' +
    '<br />' +
    '<span class="radio-button-sublabel">' +
    '${city}, ${country_code}' +
    '</span>';

  return (
    <div>
      {introContent}
      <Label>
        Country of your High School
        <Dropdown
          onChange={onCountrySelect}
          options={countries}
          value={field.value.country || ''}
        />
      </Label>
      {!!field.value.ineligibleCountry && <NoEligibleSchoolsFound />}
      {states.length > 0 && (
        <Label required>
          State
          <Dropdown
            className="states-select"
            disabled={statesLoading}
            options={states}
            onChange={onStateSelect}
            value={field.value.subdivision || ''}
          />
        </Label>
      )}
      {cities.length > 0 && (
        <Label required>
          Eligible Cities
          <Dropdown
            className="cities-select"
            disabled={citiesLoading}
            options={cities}
            onChange={onCitySelect}
            value={field.value.city || ''}
          />
        </Label>
      )}
      {highSchools.length > 0 && (
        <div className="school-list">
          <Label className="eligibility-result-count">
            {`${highSchools.length} eligible high schools found`}
            <SchoolList
              value={field.value.parentId}
              onSelect={(result) => {
                onSelect(result);
                helpers.setValue({
                  ...field.value,
                  parentId: result.id,
                  name: result.name,
                });
              }}
              schools={highSchools}
              labelTemplate={schoolLabelTemplate}
            />
          </Label>
          {allowOpenHSChooser && field.value.country === 'US' && (
            <TextButton className="cannot-find-school" onClick={openRequestInstitutionModal}>
              Still Can&apos;t Find Your School?
            </TextButton>
          )}

          {allowOpenHSChooser && field.value.country !== 'US' && (
            <a
              className="cannot-find-school"
              href="https://help.raise.me/hc/en-us/articles/360003494493-I-can-t-find-my-high-school-Non-US"
              target="_blank"
              rel="noopener noreferrer"
            >
              Still Can&apos;t Find Your School?
            </a>
          )}
        </div>
      )}
    </div>
  );
};

InstitutionChooser.propTypes = propTypes;

export default InstitutionChooser;
